/*! *********************************************************************
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *************************************************************************
 */

import _get from 'lodash/get';
import _concat from 'lodash/concat';

import auth from './auth-service';

import { fetchGraphQL, mutateGraphQL } from '../helpers/dataHelpers';
import {
  CREATE_UPDATE_THEME,
  DELETE_THEME,
  GET_THEME,
  GET_THEMES,
} from '../graphql/collectionThemes';
import uploadFile from '../services/UploadFile';

const themesProvider = {
  getList: async (resource, params) => {
    return fetchGraphQL(GET_THEMES, resource, params);
  },

  getOne: async (resource, params) => {
    return getTheme(resource, params);
  },

  getMany: (resource, params) => Promise,

  getManyReference: (resource, params) => Promise,

  create: async (resource, params) => {
    const theme = await createOrUpdate(params.data);
    return theme;
  },

  update: async (resource, params) => {
    const theme = await createOrUpdate(params.data, params.previousData);
    return theme;
  },

  updateMany: (resource, params) => Promise,

  delete: async (resource, params) => {
    return await deleteTheme(resource, params);
  },

  deleteMany: (resource, params) => Promise,
};

async function getTheme(resource, params) {
  let theme;
  let collections = [];
  let page = 1;
  // get assets chunk until all the assets have been fetch
  while (true) {
    const paramsWithPagination = {
      ...params,
      pagination: { page },
    };

    theme = await fetchGraphQL(GET_THEME, resource, paramsWithPagination);

    const themeCollectionsChunk = _get(
      theme,
      'data.themes.collections.items',
      {}
    );
    collections = _concat(collections, themeCollectionsChunk.items);

    const hasMore = _get(theme, 'data.themes.hasMore', false);
    if (!hasMore) {
      break;
    }

    ++page;
  }

  return theme;
}

async function deleteTheme(resource, params) {
  await fetchGraphQL(DELETE_THEME, resource, params);
  return { data: { id: params.id } };
}

async function createOrUpdate(theme, previousTheme) {
  const newImage = _get(theme, 'imageUrl.rawFile', null);

  let thumbnailFileId;
  if (newImage) {
    const accessToken = await auth.getAccessToken();
    const res = await uploadFile(newImage, accessToken);
    if (!res.ok) {
      throw new Error('Image upload failed');
    }

    thumbnailFileId = res.body.id;
  }

  let params = {
    status: theme.status || 'draft',
    ...(theme.title && { title: theme.title }),
    ...(theme.description && { description: theme.description }),
    ...(typeof theme.weight !== 'undefined' && { weight: theme.weight }),
    ...(thumbnailFileId && { thumbnailFileId }),
    ...(theme.collections && { collectionIds: theme.collections }),
  };

  let updateOrCreateResult;

  //GraphQL update
  updateOrCreateResult = await mutateGraphQL(
    CREATE_UPDATE_THEME,
    {
      theme: params,
      ...(previousTheme?.id && { id: previousTheme.id }),
    },
    'createOrUpdateTheme'
  );

  return updateOrCreateResult;
}

export default themesProvider;
